import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Mole {
  id: number;
  isActive: boolean;
  wasHit: boolean;
}

export function MoleGame() {
  const navigate = useNavigate();
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [highScore, setHighScore] = useState(0);
  const [moles, setMoles] = useState<Mole[]>(
    Array.from({ length: 9 }, (_, i) => ({ id: i, isActive: false, wasHit: false }))
  );
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const gameTimerRef = useRef<NodeJS.Timeout | null>(null);
  const moleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const difficultySettings = {
    easy: { interval: 1200, duration: 2000, maxActiveMoles: 1 },
    medium: { interval: 800, duration: 1500, maxActiveMoles: 2 },
    hard: { interval: 500, duration: 1000, maxActiveMoles: 3 }
  };

  useEffect(() => {
    return () => {
      if (gameTimerRef.current) clearInterval(gameTimerRef.current);
      if (moleTimerRef.current) clearTimeout(moleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (gameStarted && timeLeft > 0) {
      gameTimerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      startMoleSequence();

      return () => {
        if (gameTimerRef.current) clearInterval(gameTimerRef.current);
        if (moleTimerRef.current) clearTimeout(moleTimerRef.current);
      };
    }
  }, [gameStarted]);

  const startMoleSequence = () => {
    const settings = difficultySettings[difficulty];
    
    const activateMole = () => {
      if (!gameStarted || timeLeft <= 0) return;

      setMoles(prevMoles => {
        const activeMoles = prevMoles.filter(m => m.isActive).length;
        if (activeMoles >= settings.maxActiveMoles) return prevMoles;

        const inactiveMoles = prevMoles
          .map((mole, index) => ({ mole, index }))
          .filter(({ mole }) => !mole.isActive);

        if (inactiveMoles.length === 0) return prevMoles;

        const randomMole = inactiveMoles[Math.floor(Math.random() * inactiveMoles.length)];
        const newMoles = [...prevMoles];
        newMoles[randomMole.index] = { ...newMoles[randomMole.index], isActive: true, wasHit: false };

        // Deactivate mole after duration
        setTimeout(() => {
          setMoles(currentMoles => {
            const updated = [...currentMoles];
            if (updated[randomMole.index].isActive && !updated[randomMole.index].wasHit) {
              updated[randomMole.index] = { ...updated[randomMole.index], isActive: false };
            }
            return updated;
          });
        }, settings.duration);

        return newMoles;
      });

      moleTimerRef.current = setTimeout(activateMole, settings.interval);
    };

    activateMole();
  };

  const whackMole = (moleId: number) => {
    if (!gameStarted || timeLeft <= 0) return;

    setMoles(prevMoles => {
      const mole = prevMoles[moleId];
      if (!mole.isActive || mole.wasHit) return prevMoles;

      const newMoles = [...prevMoles];
      newMoles[moleId] = { ...mole, wasHit: true, isActive: false };

      setScore(prev => prev + 10);

      return newMoles;
    });
  };

  const endGame = () => {
    setGameStarted(false);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    if (moleTimerRef.current) clearTimeout(moleTimerRef.current);
    
    if (score > highScore) {
      setHighScore(score);
    }
  };

  const startGame = () => {
    setScore(0);
    setTimeLeft(30);
    setMoles(Array.from({ length: 9 }, (_, i) => ({ id: i, isActive: false, wasHit: false })));
    setGameStarted(true);
  };

  const renderMole = (mole: Mole) => {
    return (
      <div
        key={mole.id}
        className="relative bg-gradient-to-b from-amber-700 to-amber-900 rounded-lg shadow-lg overflow-hidden"
        style={{ aspectRatio: '1' }}
      >
        {/* Hole */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-black rounded-lg flex items-end justify-center pb-2">
          <div className="w-3/4 h-1/4 bg-gradient-to-b from-gray-900 to-black rounded-full opacity-50"></div>
        </div>

        {/* Mole */}
        <button
          onClick={() => whackMole(mole.id)}
          disabled={!mole.isActive || mole.wasHit}
          className={`
            absolute bottom-0 left-1/2 transform -translate-x-1/2
            transition-all duration-200 cursor-pointer
            ${mole.isActive && !mole.wasHit ? 'translate-y-0 hover:scale-110' : 'translate-y-full'}
            ${mole.wasHit ? 'opacity-0' : 'opacity-100'}
          `}
          style={{ width: '70%', height: '70%' }}
        >
          {/* Mole Head */}
          <div className="relative w-full h-full">
            {/* Body */}
            <div className="absolute bottom-0 w-full h-2/3 bg-gradient-to-b from-amber-800 to-amber-900 rounded-t-full border-4 border-amber-950">
              {/* Face */}
              <div className="relative h-full flex flex-col items-center justify-center">
                {/* Eyes */}
                <div className="flex gap-3 mb-1">
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                </div>
                {/* Nose */}
                <div className="w-4 h-3 bg-pink-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Hit effect */}
            {mole.wasHit && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl animate-ping">💥</span>
              </div>
            )}
          </div>
        </button>

        {/* Grass rim */}
        <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-green-600 to-green-700"></div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-6xl font-bold text-white mb-2 drop-shadow-lg">
            🔨 Whack-a-Mole 🦫
          </h1>
          <p className="text-white text-xl">Hit the moles before they disappear!</p>
        </div>

        {/* Stats Bar */}
        <div className="bg-white rounded-xl shadow-2xl p-6 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600">{score}</div>
              <div className="text-sm text-gray-600">Score</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-600">{timeLeft}s</div>
              <div className="text-sm text-gray-600">Time Left</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-yellow-600">{highScore}</div>
              <div className="text-sm text-gray-600">High Score</div>
            </div>
          </div>
        </div>

        {/* Game Board */}
        <div className="bg-green-800 rounded-xl shadow-2xl p-8 mb-6 relative">
          {!gameStarted && timeLeft > 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-70 rounded-xl flex items-center justify-center z-10">
              <div className="text-center text-white p-8">
                <h2 className="text-4xl font-bold mb-6">Ready to Play?</h2>
                
                {/* Difficulty Selection */}
                <div className="mb-6">
                  <p className="text-xl mb-4">Select Difficulty:</p>
                  <div className="flex gap-4 justify-center">
                    {(['easy', 'medium', 'hard'] as const).map(level => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`
                          px-6 py-3 rounded-lg font-semibold text-lg transition-all transform hover:scale-105
                          ${difficulty === level
                            ? 'bg-yellow-500 text-black ring-4 ring-yellow-300'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                          }
                        `}
                      >
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={startGame}
                  className="bg-green-500 hover:bg-green-600 text-white text-2xl font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                >
                  START GAME
                </button>
              </div>
            </div>
          )}

          {timeLeft === 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-90 rounded-xl flex items-center justify-center z-10">
              <div className="text-center text-white p-8">
                <h2 className="text-5xl font-bold mb-4">
                  {score >= highScore && score > 0 ? '🏆 New High Score!' : '⏰ Time\'s Up!'}
                </h2>
                <p className="text-3xl mb-8">Final Score: {score}</p>
                <div className="space-y-4">
                  <button
                    onClick={startGame}
                    className="w-full bg-green-500 hover:bg-green-600 text-white text-xl font-bold py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                  >
                    Play Again
                  </button>
                  {/* <button
                    onClick={() => navigate('/')}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white text-xl font-bold py-4 px-8 rounded-lg transition-all"
                  >
                    Back to Home
                  </button> */}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-6">
            {moles.map(mole => renderMole(mole))}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold mb-3 text-gray-800">📋 How to Play</h3>
          <ul className="space-y-2 text-gray-700">
            <li>• Click on the moles as they pop up to score points</li>
            <li>• Each hit scores 10 points</li>
            <li>• You have 30 seconds to score as many points as possible</li>
            <li>• Higher difficulty = faster moles and more at once!</li>
          </ul>
        </div>

        {/* Back Button */}
        {/* <button
          onClick={() => navigate('/')}
          className="w-full bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
        >
          ← Back to Home
        </button> */}
      </div>
    </div>
  );
}

